import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { batch, useDispatch } from 'react-redux'

import { Skeleton, Toast, Tooltip, Typography } from '@chilecompra/react-kit/components'
import { checkInputErrorRequired, useRadio } from '@chilecompra/react-kit'

import { useImperativeFormRef } from '../../modules/hooks/useFormRef'
import { dialogScrollTop } from '../../modules/utils/dialogScrollTop'
import { BINARY_CONFIRMATION_RESPONSE } from '../../config/settings/constants'

import { onOpenSustainabilityDirectDealStepTwo } from './DirectDealStepTwo.actions'

import { GridContainer, GridItem, HelpIconCustom, Radio } from './DirectDealStepTwo.styles'

/**
 * The DirectDealStepTwoSustainability's container
 */
const DirectDealStepTwoSustainability = props => {
  const { formStepTwoRef, loading, storage } = props
  const dispatch = useDispatch()

  const { onChange: handleRequirementEnviromentChange, value: requirementEnviromentValue } = useRadio({
    changeCallback: () => setEnviromentErrorRequired(''),
    errorCallbacks: [checkInputErrorRequired()],
    initialValue: storage?.requirementEnviroment || null
  })
  const { onChange: handleRequirementSocialChange, value: requirementSocialValue } = useRadio({
    changeCallback: () => setSocialErrorRequired(''),
    errorCallbacks: [checkInputErrorRequired()],
    initialValue: storage?.requirementSocial || null
  })

  const [enviromentErrorRequired, setEnviromentErrorRequired] = useState('')
  const [socialErrorRequired, setSocialErrorRequired] = useState('')

  const handleRequiredErrorEnviroment = () => {
    setEnviromentErrorRequired('Este campo es requerido.')
  }

  const handleRequiredErrorSocial = () => {
    setSocialErrorRequired('Este campo es requerido.')
  }

  const handleOpenSustainabilityDialog = () => {
    batch(() => {
      dispatch(onOpenSustainabilityDirectDealStepTwo())
      dialogScrollTop()
    })
  }

  useImperativeFormRef(
    formStepTwoRef,
    () => ({
      requirementEnviroment: {
        value: requirementEnviromentValue,
        onError: handleRequiredErrorEnviroment
      },
      requirementSocial: {
        value: requirementSocialValue,
        onError: handleRequiredErrorSocial
      }
    }),
    [requirementEnviromentValue, requirementSocialValue]
  )

  return (
    <GridContainer margin="33px 0" spacing={2}>
      <GridItem xs={12} md={2}>
        <Typography variant="body2" fontWeight="bold" tabIndex="0" color="black1">
          Compra sustentable
        </Typography>
        <Typography fontWeight="regular" variant="subtitle" padding="8px 0 0" tabIndex="0" italic color="black1">
          Esta marca no altera el proceso de compra, pero es clave para medir y visibilizar las compras sustentables en
          el Estado, en línea con los objetivos de la nueva Ley de Compras Públicas.
        </Typography>
      </GridItem>
      <GridItem lg={6} md={8} sm={12} xs={12}>
        <GridContainer spacing={1}>
          <GridItem xs={12}>
            {loading && <Skeleton variant="text" />}
            {!loading && (
              <Typography variant="body2" fontWeight="bold" tabIndex="0" color="black1">
                ¿Este Trato Directo considera requisitos medioambientales?
                <Tooltip
                  content={
                    <Typography variant="subtitle" color="white" padding="10px">
                      Se refiere a una compra que incluye requisitos que se <br />
                      enfocan en el equilibrio entre el uso de recursos naturales <br />
                      y el impacto de las actividades tienen en el medio <br />
                      ambiente, priorizando la eficiencia energética, el reciclaje <br />
                      de materiales y la reducción de la huella ecológica. Es una <br />
                      contratación que integra prácticas que minimicen el daño <br />
                      al ecosistema y promuevan el uso responsable de <br />
                      recursos naturales.
                    </Typography>
                  }
                  variant="title"
                >
                  <HelpIconCustom fontSize="22px" margin="0 0 0 10px" />
                </Tooltip>
              </Typography>
            )}
          </GridItem>
          <GridItem xl={6} lg={8} md={10} sm={10} xs={12} margin="5px 0 0 0">
            <GridContainer spacing={2}>
              <GridItem xs={12} padding="7px 8px">
                <Radio
                  direction="horizontal"
                  options={BINARY_CONFIRMATION_RESPONSE}
                  onChange={handleRequirementEnviromentChange}
                  loading={loading}
                  required
                  value={requirementEnviromentValue}
                />
                <Typography variant="subtitle" color="error">
                  {enviromentErrorRequired}
                </Typography>
              </GridItem>
            </GridContainer>
          </GridItem>
          <GridItem xs={12}>
            {loading && <Skeleton variant="text" />}
            {!loading && (
              <Typography variant="body2" fontWeight="bold" tabIndex="0" color="black1">
                ¿Este Trato Directo considera requisitos con impacto social o económico?
                <Tooltip
                  content={
                    <Typography variant="subtitle" color="white" padding="10px">
                      Se refiere a una compra que incluye requisitos <br />
                      que se enfocan en procurar mejores condiciones <br />
                      sociales, laborales y económicas, buscando, junto <br />
                      con la generación de valor económico, el <br />
                      fortalecimiento del tejido social, laboral <br />
                      y promuevan la justicia y bienestar para las <br />
                      comunidades.
                    </Typography>
                  }
                  variant="title"
                >
                  <HelpIconCustom fontSize="22px" margin="0 0 0 10px" />
                </Tooltip>
              </Typography>
            )}
          </GridItem>
          <GridItem xl={6} lg={8} md={10} sm={10} xs={12} margin="5px 0 0 0">
            <GridContainer spacing={2}>
              <GridItem xs={12} margin="5px 0 0 0" padding="7px 8px">
                <Radio
                  direction="horizontal"
                  options={BINARY_CONFIRMATION_RESPONSE}
                  onChange={handleRequirementSocialChange}
                  loading={loading}
                  required
                  value={requirementSocialValue}
                />
                <Typography variant="subtitle" color="error">
                  {socialErrorRequired}
                </Typography>
              </GridItem>
            </GridContainer>
          </GridItem>
          <GridItem xs={12}>
            {loading && <Skeleton variant="rectangular" />}
            {!loading && (
              <Toast severity="info">
                <Typography variant="body2" color="black2">
                  Consulta más información sobre estos requisitos.
                </Typography>
                <Typography
                  variant="body2"
                  color="primary"
                  fontWeight="bold"
                  style={{ textDecoration: 'underline', cursor: 'pointer' }}
                  onClick={handleOpenSustainabilityDialog}
                >
                  Ver más información
                </Typography>
              </Toast>
            )}
          </GridItem>
        </GridContainer>
      </GridItem>
    </GridContainer>
  )
}

DirectDealStepTwoSustainability.propTypes = {
  formStepTwoRef: PropTypes.shape({
    current: PropTypes.shape(Object)
  }),
  loading: PropTypes.bool,
  storage: PropTypes.shape({
    requirementEnviroment: PropTypes.string,
    requirementSocial: PropTypes.string
  })
}

export default DirectDealStepTwoSustainability
